import { useEffect, useMemo, useState, useCallback } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';

import { api } from '../../../convex/_generated/api';
import type { Id } from '../../../convex/_generated/dataModel';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { useTelemetry } from '@/hooks/useTelemetry';
import { useDebouncedAutosave } from '@/hooks/useDebouncedAutosave';
import { useToast } from '@/components/ui/use-toast';
import { friendlyAuthError } from '@/lib/auth-errors';
import { sectionNumber } from '@/lib/utils';

import { STEPS, type QuestionnaireAnswers, type StepDefinition } from './questions';
import { StepperNav } from './StepperNav';
import { LivePreview } from './LivePreview';

import { GoalStep } from './steps/GoalStep';
import { AudienceStep } from './steps/AudienceStep';
import { StyleStep } from './steps/StyleStep';
import { InspirationStep } from './steps/InspirationStep';
import { TypographyStep } from './steps/TypographyStep';
import { ColorStep } from './steps/ColorStep';
import { BorderStep } from './steps/BorderStep';
import { BrandStep } from './steps/BrandStep';
import { NavigationStep } from './steps/NavigationStep';
import { FooterStep } from './steps/FooterStep';
import { PagesStep } from './steps/PagesStep';
import { LandingContentStep } from './steps/LandingContentStep';
import { NavImportanceStep } from './steps/NavImportanceStep';
import { FeaturesStep } from './steps/FeaturesStep';

interface QuestionnaireProps {
  projectId: Id<'projects'>;
  /** Called once the user clicks the final "Continue to sketches" button. */
  onComplete: () => void;
}

// ─────────────────────────────────────────────────────────────────────────
//  Step → component mapping
// ─────────────────────────────────────────────────────────────────────────

// `brand` is intentionally absent from this registry — its component
// requires `projectId` for the optional reference-document upload, so it
// is rendered through a specialised branch in the JSX below.
const STEP_COMPONENTS: Record<
  string,
  (props: {
    answers: QuestionnaireAnswers;
    onChange: (next: Partial<QuestionnaireAnswers>) => void;
  }) => JSX.Element
> = {
  goal: GoalStep,
  audience: AudienceStep,
  style: StyleStep,
  inspiration: InspirationStep,
  typography: TypographyStep,
  color: ColorStep,
  border: BorderStep,
  navigation: NavigationStep,
  footer: FooterStep,
  pages: PagesStep,
  landing: LandingContentStep,
  'nav-importance': NavImportanceStep,
  features: FeaturesStep,
};

// ─────────────────────────────────────────────────────────────────────────
//  Completeness check per step
// ─────────────────────────────────────────────────────────────────────────

/**
 * Decide whether a step's required field(s) have been filled.
 * Optional steps return true unconditionally so they don't block progress.
 */
function isStepComplete(step: StepDefinition, a: QuestionnaireAnswers): boolean {
  if (step.optional) return true;
  switch (step.id) {
    case 'goal':
      return Boolean(a.goal && (a.goal !== 'other' || (a.goal_other ?? '').trim().length > 0));
    case 'audience':
      return (a.audience ?? []).length > 0 && (a.age_groups ?? []).length > 0;
    case 'style':
      return Boolean(a.layout_style) && Boolean(a.brand_tone);
    case 'typography':
      return Boolean(a.typography_family);
    case 'color':
      return Boolean(a.palette);
    case 'border':
      return Boolean(a.border_shape) && Boolean(a.border_line);
    case 'navigation':
      return Boolean(a.nav_link_count);
    case 'footer':
      return (a.footer_items ?? []).length > 0;
    case 'pages':
      return typeof a.page_count === 'number' && a.page_count >= 1 && a.page_count <= 10;
    case 'landing':
      return (a.landing_content ?? []).length > 0;
    case 'nav-importance':
      return Boolean(a.nav_importance);
    default:
      return true;
  }
}

// ─────────────────────────────────────────────────────────────────────────
//  Main component
// ─────────────────────────────────────────────────────────────────────────

export function Questionnaire({ projectId, onComplete }: QuestionnaireProps) {
  const { token } = useAuth();
  const { toast } = useToast();
  const telemetry = useTelemetry();

  // Hydrate from Convex.
  const stored = useQuery(
    api.questionnaire.get,
    token ? { token, projectId } : 'skip',
  );

  const upsert = useMutation(api.questionnaire.upsert);
  const markComplete = useMutation(api.questionnaire.markComplete);

  // Local state — initialised from stored row on first load.
  const [answers, setAnswers] = useState<QuestionnaireAnswers>({});
  const [currentIndex, setCurrentIndex] = useState(1);
  const [hydrated, setHydrated] = useState(false);
  const [stepEnteredAt, setStepEnteredAt] = useState<number>(Date.now());
  const [submitting, setSubmitting] = useState(false);

  // Hydration effect — runs once when Convex returns a result.
  useEffect(() => {
    if (hydrated) return;
    if (stored === undefined) return; // still loading
    if (stored === null) {
      // Brand new questionnaire — record the start.
      setHydrated(true);
      void telemetry('questionnaire.started', { projectId });
      return;
    }
    setAnswers(stored.answers ?? {});
    // Resume on the step the user was on, or the first incomplete step.
    setCurrentIndex(Math.max(1, Math.min(STEPS.length, stored.stepIndex || 1)));
    setHydrated(true);
  }, [stored, hydrated, telemetry, projectId]);

  // Reset stepEnteredAt whenever the active step changes.
  useEffect(() => {
    setStepEnteredAt(Date.now());
  }, [currentIndex]);

  // Debounced autosave — fires 600ms after the last edit.
  useDebouncedAutosave(
    { answers, currentIndex },
    async ({ answers: a, currentIndex: i }) => {
      if (!token || !hydrated) return;
      try {
        await upsert({ token, projectId, answers: a, stepIndex: i });
      } catch (err) {
        // Silent failure on autosave; show toast only if explicit save fails.
        console.warn('Autosave failed:', err);
      }
    },
    600,
    hydrated,
  );

  // Derived + handlers (must run before any early return — rules-of-hooks).
  const step = useMemo(
    () => STEPS[Math.max(0, currentIndex - 1)]!,
    [currentIndex],
  );
  const completedIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of STEPS) {
      if (isStepComplete(s, answers)) set.add(s.id);
    }
    return set;
  }, [answers]);

  const onChange = useCallback((next: Partial<QuestionnaireAnswers>) => {
    setAnswers((prev) => ({ ...prev, ...next }));
  }, []);

  const goToStep = useCallback(
    async (target: number) => {
      if (target < 1 || target > STEPS.length) return;

      const stepLeaving = STEPS[Math.max(0, currentIndex - 1)]!;

      // Telemetry for the step we're leaving.
      const elapsed = Date.now() - stepEnteredAt;
      void telemetry('questionnaire.step.completed', {
        projectId,
        payload: {
          stepId: stepLeaving.id,
          stepIndex: currentIndex,
          elapsedMs: elapsed,
        },
      });

      setCurrentIndex(target);
    },
    [stepEnteredAt, telemetry, projectId, currentIndex],
  );

  // Loading state.
  if (!token || stored === undefined) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <LoadingSpinner label="Loading your questionnaire…" />
      </div>
    );
  }

  const StepComponent = STEP_COMPONENTS[step.id];
  const requiredCompleteCount = STEPS.filter(
    (s) => !s.optional && completedIds.has(s.id),
  ).length;
  const requiredTotal = STEPS.filter((s) => !s.optional).length;
  const progressPct = Math.round((requiredCompleteCount / requiredTotal) * 100);

  const canAdvance = step.optional || isStepComplete(step, answers);
  const isLastStep = currentIndex === STEPS.length;
  const allRequiredDone = requiredCompleteCount === requiredTotal;

  const onPrev = () => goToStep(currentIndex - 1);
  const onNext = () => goToStep(currentIndex + 1);

  const onFinish = async () => {
    if (!token) return;
    if (!allRequiredDone) {
      toast({
        variant: 'destructive',
        title: 'Some required questions are not answered',
        description: 'Use the sidebar to jump to any incomplete step.',
      });
      return;
    }
    setSubmitting(true);
    try {
      // Force a final save so nothing is lost in the race with markComplete.
      await upsert({ token, projectId, answers, stepIndex: currentIndex, completed: true });
      await markComplete({ token, projectId });
      void telemetry('questionnaire.completed', { projectId });
      onComplete();
    } catch (err) {
      toast({
        variant: 'destructive',
        title: 'Could not save the questionnaire',
        description: friendlyAuthError(err),
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid grid-cols-12 gap-6 lg:gap-10">
      {/* Stepper sidebar */}
      <aside className="col-span-12 lg:col-span-2">
        <div className="sticky top-6">
          <StepperNav
            currentIndex={currentIndex}
            completedIds={completedIds}
            onJump={goToStep}
          />
        </div>
      </aside>

      {/* Main column */}
      <div className="col-span-12 lg:col-span-7">
        {/* Progress */}
        <div className="mb-10">
          <div className="mb-2 flex items-baseline justify-between">
            <p className="font-mono text-[11px] text-graphite tabular-nums">
              <span className="text-ochre">{sectionNumber(currentIndex)}</span>
              &nbsp;/&nbsp;{sectionNumber(STEPS.length)}
            </p>
            <p className="font-mono text-[11px] text-graphite tabular-nums">
              {requiredCompleteCount}/{requiredTotal} required complete
            </p>
          </div>
          <Progress value={progressPct} aria-label="Questionnaire progress" />
        </div>

        {/* Step body */}
        <div key={step.id} className="animate-fade-up">
          {step.id === 'brand' ? (
            // BrandStep needs projectId for the optional reference-document
            // upload; other steps don't, so we render it specially rather
            // than threading projectId through every step prop type.
            <BrandStep answers={answers} onChange={onChange} projectId={projectId} />
          ) : (
            <StepComponent answers={answers} onChange={onChange} />
          )}
        </div>

        {/* Footer nav */}
        <div className="mt-16 flex flex-wrap items-center justify-between gap-4 border-t border-ink/15 pt-6">
          <Button
            variant="ghost"
            onClick={onPrev}
            disabled={currentIndex === 1 || submitting}
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          <p className="font-mono text-[11px] text-graphite">
            {step.optional ? 'optional' : canAdvance ? 'ready' : 'incomplete'}
          </p>

          {isLastStep ? (
            <Button
              onClick={onFinish}
              disabled={!allRequiredDone || submitting}
              variant="accent"
            >
              {submitting ? 'Saving…' : 'Continue to sketches'}
              <Check className="h-4 w-4" />
            </Button>
          ) : (
            <Button onClick={onNext} disabled={!canAdvance || submitting}>
              Next
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>

      {/* Live preview */}
      <aside className="col-span-12 lg:col-span-3">
        <LivePreview answers={answers} onReload={() => setAnswers({ ...answers })} />
      </aside>
    </div>
  );
}
